import { css, html, LitElement } from "lit";
import { property, query, state } from "lit/decorators.js";

export class XReadMore extends LitElement {
	/** Maximum visible height (px) when collapsed */
	@property({ type: Number, attribute: "collapsed-height" })
	collapsedHeight = 120;

	/** Label for the expand action (server-side translation injected via attribute) */
	@property({ type: String, attribute: "read-more-label" })
	readMoreLabel = "Read more";

	/** Label for the collapse action (server-side translation injected via attribute) */
	@property({ type: String, attribute: "read-less-label" })
	readLessLabel = "Read less";

	/** If present, disables CSS transitions */
	@property({ type: Boolean, attribute: "no-transition", reflect: true })
	noTransition = false;

	/** If present, shows a gradient overlay when content is collapsed */
	@property({ type: Boolean, attribute: "gradient-overlay", reflect: true })
	gradientOverlay = false;

	/** Height calculation mode: 'strict' uses exact pixel values, 'proximity' rounds to nearest line height */
	@property({ type: String, attribute: "calculate-height" })
	calculateHeight: "strict" | "proximity" = "strict";

	/** If present, shows a chevron on the toggle button */
	@property({ type: Boolean, attribute: "chevron", reflect: true })
	chevron = false;

	@state()
	expanded = false;

	@state()
	needsToggle = false;

	@state()
	contentHeight = 0;

	@state()
	lineHeight = 0;

	@query(".content")
	contentEl!: HTMLElement;

	private resizeObserver?: ResizeObserver;
	private measureTimer = 0;
	private readonly contentId = `x-read-more-content-${Math.random().toString(36).slice(2, 9)}`;

	static override readonly styles = css`
		:host {
			display: block;
		}

		.wrapper {
			overflow: hidden;
			position: relative;
		}

		.content {
			overflow: hidden;
			transition: height var(--read-more-transition-duration, 300ms) cubic-bezier(0.4, 0, 0.2, 1);
		}

		:host([no-transition]) .content {
			transition: none;
		}

		.gradient-overlay {
			position: absolute;
			bottom: 0;
			left: 0;
			right: 0;
			height: 40px;
			pointer-events: none;
			opacity: 0;
			transition: opacity var(--read-more-transition-duration, 300ms) ease;
			background: linear-gradient(to bottom, transparent, var(--background));
		}

		.gradient-overlay.visible {
			opacity: 1;
		}

		.toggle {
			border: none;
			background: transparent;
			color: var(--read-more-button-color, inherit);
			padding: 0;
			margin-top: 0.5rem;
			cursor: pointer;
			font: inherit;
		}
		
	/* Chevron icon inside the toggle button */
	.toggle .chevron {
		width: var(--read-more-chevron-size, 1em);
		height: var(--read-more-chevron-size, 1em);
		margin-left: 0.5rem;
		vertical-align: middle;
		transition: transform var(--read-more-chevron-transition-duration, var(--read-more-transition-duration, 300ms)) ease;
		fill: currentColor;
	}

	/* Rotate when expanded */
	.toggle[aria-expanded="true"] .chevron {
		transform: rotate(180deg);
	}

	:host([no-transition]) .toggle .chevron {
		transition: none;
	}
	`;

	override render() {
		const collapsedHeight =
			this.calculateHeight === "proximity" && this.lineHeight > 0 ? this.roundToLineHeight(this.collapsedHeight) : this.collapsedHeight;

		const height = this.expanded && this.contentHeight > 0 ? `${this.contentHeight}px` : `${collapsedHeight}px`;

		const showGradient = this.gradientOverlay && !this.expanded && this.needsToggle;

		return html`
			<div class="wrapper">
				<div
					class="content"
					id=${this.contentId}
					role="region"
					aria-hidden=${this.expanded ? "false" : "true"}
					style="height: ${height};"
				>
					<slot @slotchange=${this.onSlotChange}></slot>
				</div>
				${showGradient ? html`<div class="gradient-overlay visible"></div>` : ""}
			</div>

			<button
				class="toggle"
				?hidden=${!this.needsToggle}
				@click=${this.toggle}
				aria-expanded=${String(this.expanded)}
				aria-controls=${this.contentId}
			>
				${this.expanded ? this.readLessLabel : this.readMoreLabel}
				${this.chevron ? html`<svg class="chevron" aria-hidden="true" viewBox="0 0 24 24" focusable="false"><path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6z"/></svg>` : ""}
			</button>
		`;
	}

	override connectedCallback() {
		super.connectedCallback();
		this.resizeObserver = new ResizeObserver(() => this.scheduleMeasure());
	}

	override disconnectedCallback() {
		super.disconnectedCallback();
		this.resizeObserver?.disconnect();
		clearTimeout(this.measureTimer);
	}

	override firstUpdated() {
		// Observe the content wrapper for size changes
		const el = this.shadowRoot?.querySelector(".content") as HTMLElement | null;
		if (el && this.resizeObserver) {
			this.resizeObserver.observe(el);
		}
		// initial measure — run immediately and again on next frame to ensure layout (fonts/images) have settled
		this.measure();
		requestAnimationFrame(() => this.measure());
	}

	private onSlotChange() {
		this.measure();
	}

	private scheduleMeasure() {
		// debounce measurements
		clearTimeout(this.measureTimer);
		this.measureTimer = globalThis.setTimeout(() => this.measure(), 50) as unknown as number;
	}

	private getLineHeight(): number {
		// Try to get line height from slotted content
		const slot = this.shadowRoot?.querySelector("slot") as HTMLSlotElement | null;
		if (slot) {
			const assignedNodes = slot.assignedNodes({ flatten: true });
			for (const node of assignedNodes) {
				if (node.nodeType === Node.ELEMENT_NODE) {
					const element = node as HTMLElement;
					const computed = window.getComputedStyle(element);
					const lineHeight = parseFloat(computed.lineHeight);
					if (!isNaN(lineHeight) && lineHeight > 0) {
						return lineHeight;
					}
				}
			}
		}

		// Fallback: try to get from content element itself
		const el = this.shadowRoot?.querySelector(".content") as HTMLElement | null;
		if (el) {
			const computed = window.getComputedStyle(el);
			const lineHeight = parseFloat(computed.lineHeight);
			if (!isNaN(lineHeight) && lineHeight > 0) {
				return lineHeight;
			}
		}

		// Final fallback: use a reasonable default (typically 1.2-1.5x font size)
		// We'll estimate based on font size
		if (el) {
			const computed = window.getComputedStyle(el);
			const fontSize = parseFloat(computed.fontSize);
			if (!isNaN(fontSize) && fontSize > 0) {
				return fontSize * 1.5; // Common line-height ratio
			}
		}

		return 20; // Absolute fallback
	}

	private roundToLineHeight(value: number): number {
		if (this.calculateHeight === "strict" || this.lineHeight === 0) {
			return value;
		}
		return Math.round(value / this.lineHeight) * this.lineHeight;
	}

	private measure() {
		// Measure the natural height of the slotted content
		const el = this.shadowRoot?.querySelector(".content") as HTMLElement | null;
		if (!el) return;

		// Get line height if using proximity mode
		if (this.calculateHeight === "proximity") {
			this.lineHeight = this.getLineHeight();
		}

		// Temporarily expand to measure full height
		const previousHeight = el.style.height;
		const previousOverflow = el.style.overflow;
		el.style.height = "auto";
		el.style.overflow = "visible";

		// Use scrollHeight as the intrinsic content height
		const measured = el.scrollHeight;
		this.contentHeight = this.roundToLineHeight(measured);

		// restore
		el.style.height = previousHeight;
		el.style.overflow = previousOverflow;

		// Compare raw measured height with raw collapsed height to determine if toggle is needed
		this.needsToggle = measured > this.collapsedHeight;

		// If expanded, keep height equal to content height so transition works predictably
		if (this.expanded && this.needsToggle && this.contentHeight > 0) {
			el.style.height = `${this.contentHeight}px`;
		}
	}

	toggle() {
		const willExpand = !this.expanded;

		// If we're about to expand, measure first to ensure smooth animation
		if (willExpand) {
			// Ensure measurement is up-to-date before expanding
			this.measure();

			// Use requestAnimationFrame to ensure measurement is complete before state change
			requestAnimationFrame(() => {
				this.expanded = willExpand;
				// Force re-render to update aria and button label
				this.requestUpdate();
				// notify listeners
				this.dispatchEvent(new CustomEvent("toggle", { detail: { expanded: this.expanded } }));
			});
		} else {
			// Collapsing - can update immediately
			this.expanded = willExpand;
			// Force re-render to update aria and button label
			this.requestUpdate();
			// notify listeners
			this.dispatchEvent(new CustomEvent("toggle", { detail: { expanded: this.expanded } }));
		}
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"x-read-more": XReadMore;
	}
}

// Register component (avoid decorator to keep TS linting happy)
if (typeof customElements !== "undefined" && !customElements.get("x-read-more")) {
	customElements.define("x-read-more", XReadMore);
}
